import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Invoice01Icon,
  UserMultiple02Icon,
  FolderDetailsIcon,
  PresentationBarChart01Icon,
  Calendar03Icon,
  Notification03Icon,
  Search01Icon,
  ExchangeDollarIcon,
  Image02Icon,
  Upload04Icon,
  Tag01Icon,
  Exchange01Icon,
  Mail01Icon,
  CheckmarkCircle02Icon,
  CreditCardIcon,
  Shield01Icon,
} from "@hugeicons/core-free-icons";
import { ArrowRight, Sparkles, Shield, Globe } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { SiteHeader } from "@/components/marketing/SiteHeader";
import { SiteFooter } from "@/components/marketing/SiteFooter";
import { InstallAppButton } from "@/components/marketing/InstallAppButton";
import { SeoHead } from "@/components/SeoHead";

const features = [
  {
    icon: Invoice01Icon,
    title: "Track Expenses",
    description:
      "Log every expense with categories, notes, images, and multiple currencies. Stay on top of your spending effortlessly.",
  },
  {
    icon: UserMultiple02Icon,
    title: "Split with Friends",
    description:
      "Share expenses one-on-one with friends and keep track of who owes what — no more awkward money conversations.",
  },
  {
    icon: FolderDetailsIcon,
    title: "Organize in Groups",
    description: "Create groups for trips, households, or events with custom icons and per-group categories.",
  },
  {
    icon: PresentationBarChart01Icon,
    title: "Visual Insights",
    description: "Spending trends, breakdowns by category, and custom date ranges show exactly where your money goes.",
  },
  {
    icon: Calendar03Icon,
    title: "Recurring Expenses",
    description: "Set up daily, weekly, or monthly recurring bills — they're created automatically every day via cron.",
  },
  {
    icon: Exchange01Icon,
    title: "Settlements",
    description: "Clear balances with friends or group members in a single tap, fully tracked in your history.",
  },
  {
    icon: Notification03Icon,
    title: "Push Notifications",
    description: "Get notified when friends add, edit, or settle expenses with you, with fine-grained preferences.",
  },
  {
    icon: ExchangeDollarIcon,
    title: "Multi-Currency",
    description: "DKK, USD, EUR and more with live exchange rates from the Frankfurter API.",
  },
  {
    icon: Upload04Icon,
    title: "CSV Import & Export",
    description:
      "Bulk-import historical expenses from a CSV file with smart fuzzy matching for categories and friends.",
  },
  {
    icon: Image02Icon,
    title: "Images & Notes",
    description: "Attach receipts and notes to any expense, stored privately and accessed via secure signed URLs.",
  },
  {
    icon: Tag01Icon,
    title: "Custom Categories",
    description: "Auto-categorize from keywords or build your own with emoji icons. Per-group categories supported.",
  },
  {
    icon: Search01Icon,
    title: "Global Search",
    description: "Find any expense, group, or friend instantly from a unified search across your entire account.",
  },
  {
    icon: Mail01Icon,
    title: "Friend Invitations",
    description: "Invite friends with a shareable link — they're auto-added to the right groups when they join.",
  },
  {
    icon: Shield01Icon,
    title: "Secure & Private",
    description: "Bank-grade encryption, strict row-level security, and your data is never shared with third parties.",
  },
];

const PRICING_FEATURES = ["Unlimited expenses & groups", "Split bills with friends"];

function isStandalonePwa() {
  if (typeof window === "undefined") return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.matchMedia?.("(display-mode: minimal-ui)").matches ||
    window.matchMedia?.("(display-mode: fullscreen)").matches ||
    nav.standalone === true
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [plan, setPlan] = useState<"monthly" | "annual">("annual");
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // When launched as an installed PWA, skip the marketing landing entirely.
  // Send to /app (auth-gated; will bounce to /auth if not signed in).
  useEffect(() => {
    if (isStandalonePwa()) {
      navigate("/app", { replace: true });
    }
  }, [navigate]);

  // Defer the supabase client (and auth check) until after first paint so
  // the marketing landing page doesn't ship the auth/db bundle in its critical path.
  useEffect(() => {
    let cancelled = false;
    const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
    const idle = (cb: () => void) => (w.requestIdleCallback ? w.requestIdleCallback(cb) : window.setTimeout(cb, 200));
    idle(async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (data.session) {
        setIsAuthenticated(true);
        navigate("/app", { replace: true });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubscribe = async () => {
    if (!isAuthenticated) {
      navigate("/auth?mode=signup");
      return;
    }
    try {
      setCheckoutLoading(true);
      // Lazy-load supabase only when user actually starts checkout
      const { supabase } = await import("@/integrations/supabase/client");
      const newTab = window.open("about:blank", "_blank");
      const { data, error } = await supabase.functions.invoke("create-checkout", { body: { plan } });
      if (error) {
        newTab?.close();
        throw error;
      }
      if (data?.url) {
        if (newTab) newTab.location.href = data.url;
        else window.location.href = data.url;
      }
    } catch {
      toast.error("Could not start checkout. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="Splitsy — Simple expense tracking & bill splitting"
        description="Track personal and shared expenses, split bills with friends, organize trips and households, and always know where your money goes."
        path="/"
      />
      <SiteHeader onFeaturesClick={() => scrollTo("features")} onPricingClick={() => scrollTo("pricing")} />

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
          <div className="max-w-4xl mx-auto px-6 pt-24 pb-20 text-center relative">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-8">
              <Sparkles className="h-4 w-4" />
              Simple expense tracking for everyone
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-foreground tracking-tight leading-[1.1] mb-6">
              Split expenses,
              <br />
              <span className="text-primary">not friendships</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
              Track personal and shared expenses in one place. Create groups, split bills, and always know where your
              money goes.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" variant="ios" onClick={() => navigate("/auth?mode=signup")}>
                Start tracking for free
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              <Button size="lg" variant="secondary" onClick={() => scrollTo("pricing")}>
                See pricing
              </Button>
            </div>
            <InstallAppButton />
          </div>
        </section>

        {/* Features */}
        <section id="features" className="py-20 bg-card/50 scroll-mt-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                Everything you need to manage money
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                From solo budgeting to group trips — Splitsy has every tool you need.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature) => (
                <div
                  key={feature.title}
                  className="bg-card rounded-2xl p-6 border border-border shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center mb-4">
                    <HugeiconsIcon icon={feature.icon} className="h-6 w-6 text-primary" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Highlights */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6">
            <div className="grid sm:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-accent mx-auto flex items-center justify-center mb-4">
                  <Globe className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Multi-Currency</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  DKK, USD, EUR and more with automatic exchange rates.
                </p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-accent mx-auto flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Secure & Private</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Your financial data is encrypted and never shared.
                </p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-accent mx-auto flex items-center justify-center mb-4">
                  <Sparkles className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Smart Categories</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Auto-categorize expenses or create your own.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="py-20 animated-gradient scroll-mt-20">
          <div className="w-full max-w-md mx-auto px-6">
            <div className="text-center mb-6">
              <div className="mb-8">
                <img
                  src="/splitsy-one-logo.png"
                  alt="Splitsy One"
                  width={420}
                  height={280}
                  loading="lazy"
                  decoding="async"
                  className="mx-auto"
                />
              </div>
              <h2 className="text-3xl font-bold text-white leading-tight">
                Your friends owe you.
                <br />
                This shouldn't too.
              </h2>
              <p className="text-white text-sm mt-3">$3.99 a month. One less thing to argue about.</p>
            </div>

        {/* Plan toggle (switch style) */}
        <div className="relative flex items-center justify-center mb-6">
          <div className="flex items-center gap-3">
            <span
              className={`text-sm font-medium transition-colors ${plan === "monthly" ? "text-foreground" : "text-white"}`}
            >
              Monthly
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={plan === "annual"}
              aria-label={`Billing period: ${plan === "annual" ? "yearly" : "monthly"}. Toggle to switch.`}
              onClick={() => setPlan(plan === "monthly" ? "annual" : "monthly")}
              className={`relative h-7 w-12 rounded-full transition-colors ${plan === "annual" ? "bg-primary" : "bg-secondary"}`}
            >
              <span
                className="absolute top-1 h-5 w-5 rounded-full bg-background shadow-sm transition-all duration-200 ease-out"
                style={{ left: plan === "annual" ? "calc(100% - 1.5rem)" : "0.25rem" }}
              />
            </button>
            <span
              className={`text-sm font-medium transition-colors ${plan === "annual" ? "text-foreground" : "text-white"}`}
            >
              Yearly
            </span>
          </div>
          <span className="absolute left-full ml-2 text-xs text-primary font-semibold bg-primary/10 px-2 py-0.5 rounded-full whitespace-nowrap">
            save 19%
          </span>
        </div>

            <div className="border-2 border-primary rounded-2xl p-6 mb-4 bg-card">
              <p className="text-xs font-semibold tracking-widest text-muted-foreground mb-2">FULL POWER</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-5xl font-bold text-foreground">{plan === "monthly" ? "$3.99" : "$3.25"}</span>
                <span className="text-base text-muted-foreground">/mo</span>
              </div>
              {plan === "annual" && (
                <p className="text-sm text-muted-foreground mb-2">$39 billed once a year</p>
              )}
              <p className="text-sm text-muted-foreground">
                One coffee. <span className="text-foreground font-medium">Zero awkward money conversations.</span>
              </p>

              <div className="border-t border-border my-5" />

              <h3 className="text-base font-semibold text-foreground mb-2">
                Cheaper than the round you bought last Friday.
              </h3>
              <p className="text-sm text-muted-foreground mb-5">
                You already paid for dinner. You already covered the Uber. Stop doing mental math at midnight — just
                use Splitsy.
              </p>

              <Button
                onClick={handleSubscribe}
                disabled={checkoutLoading}
                variant="ios"
                className="w-full h-12"
              >
                {checkoutLoading ? "Opening checkout..." : "Get Splitsy One"}
              </Button>
            </div>

            <div className="flex items-center justify-center gap-2 flex-wrap text-xs text-center">
              <span className="bg-primary/15 text-foreground font-medium px-2.5 py-1 rounded-full">14 days free</span>
              <span className="text-white/90">No card needed. Cancel anytime. No sad emails, we promise.</span>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Ready to take control of your expenses?
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
              Join Splitsy today and start tracking your spending in seconds. It's free to get started.
            </p>
            <Button size="lg" variant="ios" onClick={() => navigate("/auth?mode=signup")}>
              Get started — it's free
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>
        </section>
      </main>

      <SiteFooter onFeaturesClick={() => scrollTo("features")} onPricingClick={() => scrollTo("pricing")} />
    </div>
  );
}
